import React, { useState } from 'react';
import { storageApi } from '../../api';
import { TrashIcon } from '../icons';

export default function StoragePanel({ stats, onRefresh }) {
  const [cleaning, setCleaning]     = useState(false);
  const [cleanResult, setCleanResult] = useState(null);

  const handleCleanup = async () => {
    if (!window.confirm(`Delete all recordings older than ${stats?.retention_days ?? 7} days?`)) return;
    setCleaning(true); setCleanResult(null);
    try {
      const r = await storageApi.cleanup();
      setCleanResult(r); onRefresh();
    } catch { setCleanResult({ error: 'Cleanup failed' }); }
    finally { setCleaning(false); }
  };

  if (!stats) return null;

  const usedPct = stats.total_size_bytes
    ? Math.min((stats.total_size_bytes / (50 * 1024 * 1024 * 1024)) * 100, 100)
    : 0;

  const barColor = usedPct > 80 ? 'var(--red)' : usedPct > 60 ? 'var(--orange)' : 'var(--blue)';

  return (
    <div style={s.card}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>{stats.backend === 's3' ? 'S3 Storage' : 'Local Storage'}</h2>
          <p style={s.sub}>{stats.base_dir}</p>
        </div>
        <button onClick={onRefresh} style={s.refreshBtn}>↻ Refresh</button>
      </div>

      <div style={s.statsRow}>
        {[
          { val: stats.total_size_display || '0 B', label: 'Used' },
          { val: stats.file_count ?? 0,             label: 'Recordings' },
          { val: `${stats.retention_days ?? 7} days`, label: 'Retention' },
          { val: `${usedPct.toFixed(1)}%`,           label: 'Capacity' },
        ].map(({ val, label }) => (
          <div key={label} style={s.stat}>
            <div style={s.statVal}>{val}</div>
            <div style={s.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Usage bar */}
      <div style={s.barWrap}>
        <div style={s.barTrack}>
          <div style={{ ...s.barFill, width: `${usedPct}%`, background: barColor }} />
        </div>
        <span style={s.barLabel}>of estimated 50 GB</span>
      </div>

      {/* Cleanup */}
      <div style={s.cleanRow}>
        <button
          onClick={handleCleanup}
          disabled={cleaning}
          style={{ ...s.cleanBtn, opacity: cleaning ? 0.6 : 1 }}
        >
          {cleaning ? 'Running…' : <><TrashIcon size={14} style={{ marginRight: 6, verticalAlign: -2 }} />Run Cleanup</>}
        </button>
        <span style={s.cleanHint}>
          Removes files older than {stats.retention_days ?? 7} days automatically at 3 AM daily
        </span>
      </div>

      {cleanResult && (
        <div style={cleanResult.error ? s.resultErr : s.resultOk}>
          {cleanResult.error
            ? `Error: ${cleanResult.error}`
            : `✓ Deleted ${cleanResult.deleted_count} file(s) · Freed ${cleanResult.freed_display}`
          }
        </div>
      )}
    </div>
  );
}

const s = {
  card: {
    background: 'var(--white)', borderRadius: 'var(--radius)',
    border: '1px solid var(--gray-200)', padding: '24px',
    boxShadow: 'var(--shadow-sm)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start', marginBottom: 20,
  },
  title: { fontSize: 16, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 3 },
  sub: { fontSize: 12, color: 'var(--gray-400)', fontFamily: 'monospace' },
  refreshBtn: {
    background: 'var(--gray-100)', border: '1px solid var(--gray-200)',
    borderRadius: 'var(--radius-sm)', padding: '6px 13px',
    fontSize: 13, cursor: 'pointer', color: 'var(--gray-600)',
    fontFamily: 'var(--font)',
  },
  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 12, marginBottom: 20,
  },
  stat: {
    background: 'var(--gray-50)', borderRadius: 'var(--radius-sm)',
    padding: '14px 16px',
  },
  statVal: { fontSize: 20, fontWeight: 700, color: 'var(--gray-900)', marginBottom: 3 },
  statLabel: { fontSize: 11, color: 'var(--gray-400)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 },
  barWrap: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 },
  barTrack: {
    flex: 1, height: 6, background: 'var(--gray-100)',
    borderRadius: 3, overflow: 'hidden',
  },
  barFill: { height: '100%', borderRadius: 3, transition: 'width 0.5s ease' },
  barLabel: { fontSize: 12, color: 'var(--gray-400)', whiteSpace: 'nowrap' },
  cleanRow: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
  cleanBtn: {
    background: 'var(--red-light)', color: 'var(--red)',
    border: '1px solid rgba(239,68,68,0.2)',
    borderRadius: 'var(--radius-sm)', padding: '8px 16px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'var(--font)',
  },
  cleanHint: { fontSize: 13, color: 'var(--gray-400)' },
  resultOk: {
    marginTop: 12, padding: '10px 14px',
    background: 'var(--green-light)', color: 'var(--green)',
    borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500,
  },
  resultErr: {
    marginTop: 12, padding: '10px 14px',
    background: 'var(--red-light)', color: 'var(--red)',
    borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 500,
  },
};
